import { GuardiansStackParamList } from "@/types/guardiansNavigation"

/** Маппинг экранов gDaksby → URL gDaksbyWeb */
export function screenNameToPath(
  screen: keyof GuardiansStackParamList,
  params?: GuardiansStackParamList[keyof GuardiansStackParamList]
): string {
  const q = (entries: Record<string, string | number | undefined>) => {
    const sp = new URLSearchParams()
    for (const [k, v] of Object.entries(entries)) {
      if (v != null && v !== "") sp.set(k, String(v))
    }
    const s = sp.toString()
    return s ? `?${s}` : ""
  }

  switch (screen) {
    case "GuardiansTabs":
      if (params && typeof params === "object" && "screen" in params) {
        const tab = (params as { screen?: string }).screen
        if (tab === "GuardiansMap") return "/play/map"
        if (tab === "GuardiansCollection") return "/play/collection"
        if (tab === "GuardiansProfile") return "/play/profile"
      }
      return "/play"
    case "Onboarding":
      return "/play/onboarding"
    case "AffiliationSelection":
      return "/play/onboarding/affiliation"
    case "GuardianPathSelection":
      return "/play/onboarding/path"
    case "StarterCards":
      return "/play/onboarding/starter"
    case "Duel":
      return "/play/duel"
    case "Matchmaking": {
      if (params && typeof params === "object") {
        const p = params as GuardiansStackParamList["Matchmaking"]
        return `/play/matchmaking${q({
          mode: p.mode,
          role: p.role,
        })}`
      }
      return "/play/matchmaking"
    }
    case "DeckDuel":
      return "/play/deck-duel"
    case "DeckBuilder":
      return "/play/deck-builder"
    case "DeckMatchmaking":
      if (params && typeof params === "object") {
        const p = params as GuardiansStackParamList["DeckMatchmaking"]
        const cardIds = p.cardIds ?? []
        const sp = new URLSearchParams()
        sp.set("deckSearchMode", p.deckSearchMode)
        if (p.colorRank) sp.set("colorRank", p.colorRank)
        cardIds.forEach((id) => sp.append("cardIds", id))
        if (p.autoStart) sp.set("autoStart", "1")
        const qs = sp.toString()
        return `/play/deck-matchmaking${qs ? `?${qs}` : ""}`
      }
      return "/play/deck-matchmaking"
    case "BattleLobby":
      return "/play"
    case "ChallengeWaiting":
      return "/play/challenge-waiting"
    case "DeckChallengeWaiting":
      return "/play/deck-challenge-waiting"
    case "Shop":
      return "/play/shop"
    case "Academy":
      return "/play/academy"
    case "DailyQuiz":
      return "/play/academy"
    case "DailyQuests":
      return "/play/daily-quests"
    case "Leaderboard":
      return "/play/leaderboard"
    case "PastChampions": {
      if (params && typeof params === "object" && "cityId" in params) {
        const p = params as GuardiansStackParamList["PastChampions"]
        return `/play/past-champions${q({ cityId: p?.cityId })}`
      }
      return "/play/past-champions"
    }
    case "CityChat":
      return "/play/city-chat"
    case "Guild":
      return "/play/guild"
    case "GameNotifications":
      return "/play/notifications"
    case "GameNotificationDetail":
      return "/play/notifications/detail"
    case "Redeem":
      return "/play/redeem"
    case "LocationScan":
      return "/play/location-scan"
    case "BossMap": {
      if (params && typeof params === "object" && "focusBossId" in params) {
        return `/play/boss-map${q({
          focusBossId: String(params.focusBossId ?? ""),
        })}`
      }
      return "/play/boss-map"
    }
    case "WorldBoss":
      return "/play/world-boss"
    case "CardShop":
      return "/play/card-shop"
    case "BoosterShops":
      return "/play/booster-shop"
    case "CardShopCatalog":
    case "PostOffice":
    case "Bank":
    case "CityHall":
      if (params && typeof params === "object" && "pointId" in params) {
        const pointId = String(params.pointId)
        const base =
          screen === "PostOffice"
            ? "/play/post-office"
            : screen === "Bank"
              ? "/play/bank"
              : screen === "CityHall"
                ? "/play/city-hall"
                : "/play/card-shop"
        return `${base}${q({ pointId })}`
      }
      return screen === "CityHall" ? "/play/city-hall" : "/play/card-shop"
    case "Airship":
      return "/play/airship"
    case "Hangar":
      if (params && typeof params === "object" && "pointId" in params) {
        return `/play/hangar${q({ pointId: String(params.pointId) })}`
      }
      return "/play/hangar"
    case "Hotel":
      if (params && typeof params === "object" && "pointId" in params) {
        return `/play/hotel${q({ pointId: String(params.pointId) })}`
      }
      return "/play/hotel"
    case "WiseTree":
      if (params && typeof params === "object" && "pointId" in params) {
        return `/play/wise-tree${q({ pointId: String(params.pointId) })}`
      }
      return "/play/wise-tree"
    case "Help":
      return "/play/help"
    case "GameRules":
      return "/play/rules"
    case "VirtualTournament":
      return "/play/virtual-tournament"
    case "VirtualTournamentDetail":
      if (params && typeof params === "object" && "tournamentId" in params) {
        const p = params as GuardiansStackParamList["VirtualTournamentDetail"]
        return `/play/virtual-tournament/${encodeURIComponent(p.tournamentId)}${q({
          colorRank: p.colorRank,
        })}`
      }
      return "/play/virtual-tournament"
    case "DailyVirtualTournament":
      return "/play/daily-tournament"
    case "DailyVirtualTournamentDetail":
      if (params && typeof params === "object" && "tournamentId" in params) {
        const p = params as GuardiansStackParamList["DailyVirtualTournamentDetail"]
        return `/play/daily-tournament/${encodeURIComponent(p.tournamentId)}${q({
          colorRank: p.colorRank,
        })}`
      }
      return "/play/daily-tournament"
    case "PublicProfile":
      if (params && typeof params === "object" && "code" in params && params.code) {
        return `/play/player/${encodeURIComponent(String(params.code))}`
      }
      return "/play/profile"
    case "Almanac":
      if (params && typeof params === "object" && "publicGameCode" in params && params.publicGameCode) {
        return `/play/almanac/${encodeURIComponent(String(params.publicGameCode))}`
      }
      return "/play/almanac"
    case "CardDetail":
      if (params && typeof params === "object" && "cardId" in params) {
        return `/play/cards/${encodeURIComponent(String(params.cardId))}`
      }
      return "/play/collection"
    case "RankExam":
      if (params && typeof params === "object" && "cardId" in params) {
        return `/play/cards/${encodeURIComponent(String(params.cardId))}/rank-exam`
      }
      return "/play/collection"
    case "FuseCards":
      if (params && typeof params === "object" && "mainCardId" in params) {
        return `/play/fuse${q({ mainCardId: String(params.mainCardId) })}`
      }
      return "/play/fuse"
    case "CardWardrobe":
      return "/play/wardrobe"
    case "ShardsHub":
      return "/play/shards"
    case "ShardCraft":
      if (params && typeof params === "object") {
        const rec = params as Record<string, unknown>
        return `/play/shards${q({
          element:
            typeof rec.element === "string" ? rec.element : undefined,
          colorRank:
            typeof rec.colorRank === "string" ? rec.colorRank : undefined,
        })}`
      }
      return "/play/shards"
    case "DustShop":
      return "/play/dust-shop"
    case "PostOfficeCreateLot":
      if (params && typeof params === "object" && "pointId" in params) {
        return `/play/post-office/create-lot${q({
          pointId: String(params.pointId),
        })}`
      }
      return "/play/post-office/create-lot"
    case "WarDashboard":
      return "/play/war"
    case "BattleHistory":
      return "/play/battle-history"
    case "Referral":
      return "/play/referral"
    case "Pet":
    case "PetLab":
      return "/play/pet"
    case "TrainingGym":
      return "/play/training"
    case "ArenaList":
      return "/play/arena"
    case "Arena":
      if (params && typeof params === "object" && "arenaId" in params) {
        return `/play/arena/${encodeURIComponent(String(params.arenaId))}`
      }
      return "/play/arena"
    case "CityChronicle":
      if (params && typeof params === "object" && "cityId" in params && params.cityId != null) {
        return `/play/city-chronicle${q({ cityId: Number(params.cityId) })}`
      }
      return "/play/city-chronicle"
    case "CityHallRaidBattle":
      if (params && typeof params === "object" && "raidId" in params) {
        return `/play/city-hall/raid/${encodeURIComponent(String(params.raidId))}`
      }
      return "/play/city-hall/raid"
    default:
      return `/play/hub/${String(screen).replace(/([A-Z])/g, "-$1").toLowerCase()}`
  }
}
